import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { HMSRoomProvider } from '@100mslive/react-sdk';
import { Package, CreditCard, AlertTriangle, X } from 'lucide-react';
import { parseUTC } from '../utils/date';
import { VideoPreview } from './VideoPreview';
import { AuctionControls } from './AuctionControls';
import { AuctionOverlay } from './AuctionOverlay';
import { StatsOverlay } from './StatsOverlay';
import { FloatingSideMenu } from './FloatingSideMenu';
import { ShowEndedOverlay } from './ShowEndedOverlay';
import { ControlsModal } from './ControlsModal';
import { SellerNotesModal } from './SellerNotesModal';
import { GiveawayModal } from './GiveawayModal';
import { ShareModal } from './ShareModal';
import { ViewerList } from './ViewerList';
import { Chat } from './Chat';
import { Header } from './Header';
import { useWebSocket } from '../hooks/useWebSocket';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import {
  getShow, startShow, endShow, getHmsToken, putItemOnDeck, listListings,
  createItemFromListing, createAuction, updateSellerNotes, getSalesByShow,
  cancelSale, endBinSale, getPublicProfile, ApiError,
} from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { colors } from '../constants/colors';
import type { Show, Item, Listing, ItemSaleWithPayment, PaymentStatus } from '../types';
import { Img } from './Img';

// Helper to check if error is about Stripe onboarding
function isStripeOnboardingError(errorMessage: string): boolean {
  return errorMessage.toLowerCase().includes('stripe') ||
         errorMessage.toLowerCase().includes('onboarding') ||
         errorMessage.toLowerCase().includes('payment');
}

// Helper to get payment status color
function getPaymentStatusColor(status: PaymentStatus | undefined): string {
  switch (status) {
    case 'PENDING': return '#F59E0B';
    case 'AUTHORIZED': return '#3B82F6';
    case 'CAPTURED': return '#22C55E';
    case 'FAILED': return '#DC2626';
    case 'CANCELLED': return '#6B7280';
    case 'REFUNDED': return '#8B5CF6';
    case 'PARTIALLY_REFUNDED': return '#A855F7';
    default: return '#6B7280';
  }
}

// Helper to get payment status label
function getPaymentStatusLabel(status: PaymentStatus | undefined): string {
  switch (status) {
    case 'PENDING': return 'Processing';
    case 'AUTHORIZED': return 'Authorized';
    case 'CAPTURED': return 'Paid';
    case 'FAILED': return 'Failed';
    case 'CANCELLED': return 'Cancelled';
    case 'REFUNDED': return 'Refunded';
    case 'PARTIALLY_REFUNDED': return 'Partial Refund';
    default: return 'Unknown';
  }
}

export function SellerDashboard() {
  useDocumentTitle('Live Dashboard');
  const navigate = useNavigate();
  const { showId } = useParams<{ showId: string }>();
  const { user } = useAuth();
  const [show, setShow] = useState<Show | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rightPanelTab, setRightPanelTab] = useState<'chat' | 'viewers'>('chat');
  const [leftPanelTab, setLeftPanelTab] = useState<'inventory' | 'sales'>('inventory');
  const [listings, setListings] = useState<Listing[]>([]);
  const [showSales, setShowSales] = useState<ItemSaleWithPayment[]>([]);
  const [salesLoading, setOrdersLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  const [listingsLoading, setListingsLoading] = useState(false);
  const [cancellingSaleId, setCancellingSaleId] = useState<string | null>(null);
  const [activeModal, setActiveModal] = useState<'controls' | 'share' | 'notes' | 'giveaway' | null>(null);
  const [statsMinimized, setStatsMinimized] = useState(false);
  const [endBinLoading, setEndBinLoading] = useState(false);
  const [highBidderAvatarUrl, setHighBidderAvatarUrl] = useState<string | null>(null);
  const [winnerAvatarUrl, setWinnerAvatarUrl] = useState<string | null>(null);
  const peakViewerCount = useRef(0);

  const {
    isConnected,
    viewerCount,
    viewers,
    onDeckItem,
    activeAuction,
    activeBinSale,
    recentBids,
    auctionEnded,
    activeGiveaway,
    giveawayEnded,
    clearGiveawayEnded,
    showEnded,
    connect,
    disconnect,
    connectionId,
    broadcastDeviceId,
    claimBroadcast,
  } = useWebSocket();

  const isBroadcasting = connectionId != null && connectionId === broadcastDeviceId;

  // Track peak viewer count
  useEffect(() => {
    if (viewerCount > peakViewerCount.current) {
      peakViewerCount.current = viewerCount;
    }
  }, [viewerCount]);

  // Auto-claim broadcast when no device is broadcasting
  useEffect(() => {
    if (connectionId && (broadcastDeviceId === null || broadcastDeviceId === '')) {
      claimBroadcast();
    }
  }, [connectionId, broadcastDeviceId, claimBroadcast]);

  // Load show on mount
  useEffect(() => {
    if (!showId) {
      navigate('/sell/live');
      return;
    }

    const loadShow = async () => {
      try {
        setLoading(true);
        const showData = await getShow(showId);
        setShow(showData);

        // If show is already live, connect to WebSocket and get HMS token
        if (showData.status === 'LIVE') {
          // Connect using JWT token (the hook will use getAccessToken)
          connect(showId);
          if (showData.hmsRoomId) {
            try {
              const tokenResponse = await getHmsToken(showId);
              setAuthToken(tokenResponse.authToken);
            } catch (e) {
              console.warn('Failed to get HMS token:', e);
            }
          }
        }
      } catch (e) {
        setError('Failed to load show. It may not exist.');
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    loadShow();

    return () => {
      setAuthToken(null);
      disconnect();
    };
  }, [showId, navigate, connect, disconnect]);

  // Connect to WebSocket when show becomes live
  useEffect(() => {
    if (show?.status === 'LIVE' && !isConnected && showId) {
      // Connect using JWT token
      connect(showId);
    }
  }, [show?.status, showId, isConnected, connect]);

  const loadSalesData = useCallback(async () => {
    if (!showId) return;
    setOrdersLoading(true);
    try {
      const sales = await getSalesByShow(showId);
      // Sort by createdAt descending (newest first)
      sales.sort((a, b) => parseUTC(b.createdAt).getTime() - parseUTC(a.createdAt).getTime());
      setShowSales(sales);
    } catch (e) {
      console.error('Failed to load sales:', e);
    } finally {
      setOrdersLoading(false);
    }
  }, [showId]);

  const loadListingsData = useCallback(async () => {
    setListingsLoading(true);
    try {
      // Load listings that have stock available
      const allListings = await listListings(false);
      const availableListings = allListings.filter(l => l.stock > 0);
      setListings(availableListings);

      // Update selectedListing with fresh data (to get updated stock count)
      setSelectedListing(prev => {
        if (!prev) return null;
        const updated = allListings.find(l => l.id === prev.id);
        return updated || prev;
      });
    } catch (e) {
      console.error('Failed to load listings:', e);
    } finally {
      setListingsLoading(false);
    }
  }, []);

  // Load listings when show becomes live
  useEffect(() => {
    if (!showId || show?.status !== 'LIVE') return;

    loadListingsData();
  }, [showId, show?.status, loadListingsData]);

  // Refresh listings and orders after auction ends (to update stock counts and show new orders)
  useEffect(() => {
    if (auctionEnded && show?.status === 'LIVE') {
      loadListingsData();
      // Refresh orders if there was a winner (new order created)
      if (auctionEnded.winnerId) {
        loadSalesData();
      }
    }
  }, [auctionEnded, show?.status, loadListingsData, loadSalesData]);

  // Refresh listings and orders after giveaway ends with a winner
  useEffect(() => {
    if (giveawayEnded && !giveawayEnded.cancelled && giveawayEnded.winnerId && show?.status === 'LIVE') {
      loadListingsData();
      loadSalesData();
    }
  }, [giveawayEnded, show?.status, loadListingsData, loadSalesData]);

  // Handle unexpected show end (server-side termination)
  useEffect(() => {
    if (showEnded) {
      // Kill HMS video stream
      setAuthToken(null);
      // Update show status so UI switches to ended state
      setShow(prev => prev ? { ...prev, status: 'ENDED' } : prev);
      disconnect();
    }
  }, [showEnded, disconnect]);

  // Refresh orders when BIN sale quantity decreases (purchase happened)
  const prevBinQuantityRef = useRef<number | null>(null);
  useEffect(() => {
    if (!activeBinSale || show?.status !== 'LIVE') {
      prevBinQuantityRef.current = null;
      return;
    }

    const prevQuantity = prevBinQuantityRef.current;
    const currentQuantity = activeBinSale.remainingQuantity;

    // If quantity decreased, a purchase happened - refresh orders
    if (prevQuantity !== null && currentQuantity < prevQuantity) {
      loadSalesData();
      loadListingsData();
    }

    prevBinQuantityRef.current = currentQuantity;
  }, [activeBinSale?.remainingQuantity, activeBinSale, show?.status, loadSalesData, loadListingsData]);

  // Load orders when show becomes live (for sales summary)
  useEffect(() => {
    if (show?.status === 'LIVE') {
      loadSalesData();
    }
  }, [show?.status, loadSalesData]);

  // Reload orders when switching to orders tab (in case of stale data)
  useEffect(() => {
    if (leftPanelTab === 'sales' && show?.status === 'LIVE') {
      loadSalesData();
    }
  }, [leftPanelTab, show?.status, loadSalesData]);

  // Fetch high bidder avatar
  useEffect(() => {
    const username = activeAuction?.highestBidderUsername;
    if (!username) { setHighBidderAvatarUrl(null); return; }
    getPublicProfile(username)
      .then((profile) => setHighBidderAvatarUrl(profile.avatarUrl || null))
      .catch(() => {});
  }, [activeAuction?.highestBidderUsername]);

  // Fetch winner avatar
  useEffect(() => {
    const username = auctionEnded?.winnerUsername;
    if (!username || !auctionEnded?.winnerId) { setWinnerAvatarUrl(null); return; }
    getPublicProfile(username)
      .then((profile) => setWinnerAvatarUrl(profile.avatarUrl || null))
      .catch(() => {});
  }, [auctionEnded?.winnerUsername, auctionEnded?.winnerId]);

  const handleSelectListing = async (listing: Listing) => {
    if (!showId || activeAuction) return;
    try {
      // Create item from listing
      const item = await createItemFromListing(showId, { listingId: listing.id });

      // Put item on deck
      await putItemOnDeck(showId, {
        id: item.id,
        name: item.name,
        description: item.description,
        imageUrl: item.imageUrl,
        startingPrice: item.startingPrice,
      });

      setSelectedItem(item);
      setSelectedListing(listing);
    } catch (e) {
      console.error('Failed to create item from listing:', e);
    }
  };

  // Create a new item from listing and immediately start auction
  const handleRunAnotherAuction = async (
    listing: Listing,
    params: { startingPrice: number; auctionDuration: number; counterbidTime: number; suddenDeathEnabled: boolean }
  ) => {
    if (!showId) return;
    try {
      // Create item from listing
      const item = await createItemFromListing(showId, { listingId: listing.id });

      // Put item on deck
      await putItemOnDeck(showId, {
        id: item.id,
        name: item.name,
        description: item.description,
        imageUrl: item.imageUrl,
        startingPrice: item.startingPrice,
      });

      // Start auction immediately with given parameters
      await createAuction(
        showId,
        item.id,
        params.startingPrice,
        params.auctionDuration,
        params.counterbidTime,
        params.suddenDeathEnabled
      );

      // Update state for tracking
      setSelectedItem(item);
      setSelectedListing(listing);
    } catch (e) {
      console.error('Failed to run another auction:', e);
      throw e;
    }
  };

  const handleStartShow = async () => {
    if (!show) return;
    setActionLoading(true);
    setError(null);
    try {
      const updatedShow = await startShow(show.id);
      setShow(updatedShow);

      // Connect using JWT token
      connect(show.id);

      if (updatedShow.hmsRoomId) {
        const tokenResponse = await getHmsToken(updatedShow.id);
        setAuthToken(tokenResponse.authToken);
      }
    } catch (e) {
      // Handle 409 Conflict -- seller already has a different live show
      if (e instanceof ApiError && e.status === 409 && e.data.activeShowId) {
        navigate(`/sell/live/${e.data.activeShowId}`);
        return;
      }
      const errorMessage = e instanceof Error ? e.message : 'Failed to start show';
      setError(errorMessage);
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  const handleEndShow = async () => {
    if (!show) return;
    setActionLoading(true);
    try {
      const updatedShow = await endShow(show.id);
      setShow(updatedShow);
      setAuthToken(null);
      setActiveModal(null);
      disconnect();
    } catch (e) {
      setError('Failed to end show');
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };



  const handleCancelSale = async (saleId: string) => {
    if (cancellingSaleId) return;

    if (!confirm('Are you sure you want to cancel this sale?')) return;

    setCancellingSaleId(saleId);
    try {
      await cancelSale(saleId);
      // Refresh sales list
      await loadSalesData();
    } catch (err) {
      console.error('Failed to cancel sale:', err);
      alert('Failed to cancel sale. Please try again.');
    } finally {
      setCancellingSaleId(null);
    }
  };

  const handleEndBinSale = async () => {
    if (!showId || !activeBinSale) return;
    setEndBinLoading(true);
    try {
      await endBinSale(showId, activeBinSale.id);
    } catch (e) {
      console.error('Failed to end BIN sale:', e);
    } finally {
      setEndBinLoading(false);
    }
  };

  const displayItem = onDeckItem || selectedItem;
  const overlayStartingPrice = selectedItem?.startingPrice || selectedListing?.startingPrice || 10;
  const totalSales = showSales.reduce((sum, order) => sum + order.winningBid, 0);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingState}>
          <p>Loading show...</p>
        </div>
      </div>
    );
  }

  if (error && !show) {
    return (
      <div style={styles.container}>
        <div style={styles.errorState}>
          <p style={styles.errorText}>{error}</p>
          <button onClick={() => navigate('/sell/live')} style={styles.secondaryButton}>
            Create New Show
          </button>
        </div>
      </div>
    );
  }

  return (
    <HMSRoomProvider>
      <div style={styles.container}>
        <Header />

        <main style={{
          ...styles.main,
          gridTemplateColumns: show?.status === 'LIVE' ? '340px 1fr 340px' : '1fr 340px',
        }}>
          {/* Left Column - Inventory / Sales (only when LIVE) */}
          {show?.status === 'LIVE' && (
            <div style={styles.leftColumn}>
              {/* Tab Switcher */}
              <div style={styles.leftTabSwitcher}>
                <button
                  style={{
                    ...styles.leftTabButton,
                    ...(leftPanelTab === 'inventory' ? styles.leftTabButtonActive : {}),
                  }}
                  onClick={() => setLeftPanelTab('inventory')}
                >
                  Inventory
                </button>
                <button
                  style={{
                    ...styles.leftTabButton,
                    ...(leftPanelTab === 'sales' ? styles.leftTabButtonActive : {}),
                  }}
                  onClick={() => setLeftPanelTab('sales')}
                >
                  Sales {showSales.length > 0 && `(${showSales.length})`}
                </button>
              </div>

              {/* Inventory Tab */}
              {leftPanelTab === 'inventory' && (
                <div style={styles.scrollableContent}>
                  {listingsLoading ? (
                    <p style={styles.storeHint}>Loading inventory...</p>
                  ) : listings.length === 0 ? (
                    <p style={styles.storeHint}>No listings with stock. Add inventory in Seller Hub.</p>
                  ) : (
                    <div style={styles.itemsList}>
                      {listings.map((listing) => (
                        <div
                          key={listing.id}
                          style={{
                            ...styles.storeItem,
                            ...(selectedListing?.id === listing.id ? styles.storeItemSelected : {}),
                            ...(activeAuction ? styles.storeItemDisabled : {}),
                          }}
                          onClick={() => !activeAuction && handleSelectListing(listing)}
                        >
                          {listing.imageUrl ? (
                            <Img src={listing.imageUrl} alt={listing.name} variant="thumb" renderedWidth={48} blurhash={listing.blurhash} style={styles.storeItemImage} />
                          ) : (
                            <div style={styles.storeItemImagePlaceholder}>
                              <Package size={20} color={colors.gray[400]} />
                            </div>
                          )}
                          <div style={styles.storeItemInfo}>
                            <span style={styles.storeItemName}>{listing.name}</span>
                            <span style={styles.storeItemPrice}>${listing.startingPrice}</span>
                            <span style={styles.storeItemStock}>
                              {listing.isUnique ? 'Unique' : `${listing.stock} in stock`}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Sales Tab */}
              {leftPanelTab === 'sales' && (
                <div style={styles.scrollableContent}>
                  {salesLoading ? (
                    <p style={styles.storeHint}>Loading orders...</p>
                  ) : showSales.length === 0 ? (
                    <p style={styles.storeHint}>No sales yet. Sales will appear here when items sell.</p>
                  ) : (
                    <div style={styles.itemsList}>
                      {/* Failed orders first */}
                      {showSales.filter(o => o.paymentStatus === 'FAILED').length > 0 && (
                        <div style={styles.failedOrdersSection}>
                          <div style={styles.failedOrdersHeader}>
                            <AlertTriangle size={14} color="#DC2626" />
                            <span style={styles.failedOrdersTitle}>Payment Failed</span>
                          </div>
                          {showSales.filter(o => o.paymentStatus === 'FAILED').map((order) => (
                            <div key={order.id} style={styles.orderItemFailed}>
                              {order.itemSnapshot?.imageUrl ? (
                                <Img src={order.itemSnapshot.imageUrl} alt={order.itemSnapshot.name} variant="thumb" renderedWidth={48} blurhash={order.itemSnapshot.blurhash} style={styles.storeItemImage} />
                              ) : (
                                <div style={styles.storeItemImagePlaceholder}>
                                  <Package size={20} color={colors.gray[400]} />
                                </div>
                              )}
                              <div style={styles.orderItemInfo}>
                                <span style={styles.storeItemName}>{order.itemSnapshot?.name ?? 'Unknown Item'}</span>
                                <span style={styles.orderItemBuyer}>{order.buyerUsername || order.buyerId.slice(0, 8)}</span>
                              </div>
                              <div style={styles.orderItemRight}>
                                <span style={styles.orderItemPrice}>${order.winningBid.toFixed(2)}</span>
                                <button
                                  style={styles.cancelSaleButton}
                                  onClick={() => handleCancelSale(order.id)}
                                  disabled={cancellingSaleId === order.id}
                                  title="Cancel sale"
                                >
                                  {cancellingSaleId === order.id ? '...' : <X size={14} />}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Other orders */}
                      {showSales.filter(o => o.paymentStatus !== 'FAILED').map((order) => (
                        <div key={order.id} style={styles.orderItem}>
                          {order.itemSnapshot?.imageUrl ? (
                            <Img src={order.itemSnapshot.imageUrl} alt={order.itemSnapshot.name} variant="thumb" renderedWidth={48} blurhash={order.itemSnapshot.blurhash} style={styles.storeItemImage} />
                          ) : (
                            <div style={styles.storeItemImagePlaceholder}>
                              <Package size={20} color={colors.gray[400]} />
                            </div>
                          )}
                          <div style={styles.orderItemInfo}>
                            <span style={styles.storeItemName}>{order.itemSnapshot?.name ?? 'Unknown Item'}</span>
                            <span style={styles.orderItemBuyer}>{order.buyerUsername || order.buyerId.slice(0, 8)}</span>
                          </div>
                          <div style={styles.orderItemRight}>
                            <span style={styles.orderItemPrice}>${order.winningBid.toFixed(2)}</span>
                            <span style={{
                              ...styles.orderItemStatus,
                              color: getPaymentStatusColor(order.paymentStatus),
                            }}>
                              {getPaymentStatusLabel(order.paymentStatus)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

            </div>
          )}

          {/* Center Column - Video with overlays */}
          <div style={styles.centerColumn}>
            {/* Stripe Onboarding Error Card */}
            {error && isStripeOnboardingError(error) && (
              <div style={styles.onboardingErrorCard}>
                <div style={styles.onboardingErrorHeader}>
                  <div style={styles.onboardingErrorIcon}>
                    <CreditCard size={24} color="#DC2626" />
                  </div>
                  <div>
                    <h3 style={styles.onboardingErrorTitle}>Payment Setup Required</h3>
                    <p style={styles.onboardingErrorText}>
                      You need to complete your payment setup before starting a show.
                      This allows you to receive payments from your sales.
                    </p>
                  </div>
                </div>
                <Link to="/sell/settings" style={styles.onboardingErrorButton}>
                  <CreditCard size={18} />
                  Set Up Payments
                </Link>
              </div>
            )}

            {error && !isStripeOnboardingError(error) && (
              <p style={styles.error}>{error}</p>
            )}

            <div style={styles.videoSection}>
              <VideoPreview
                authToken={authToken}
                userName={user?.username || 'Seller'}
                isBroadcasting={isBroadcasting}
                onClaimBroadcast={claimBroadcast}
              />

              {/* Top gradient */}
              <div style={styles.topGradient} />
              {/* Bottom gradient */}
              <div style={styles.bottomGradient} />

              {/* Connected pill (top-right) — LIVE badge is rendered by VideoPreview */}
              {show?.status === 'LIVE' && (
                <div style={{ position: 'absolute', top: 14, right: 14, zIndex: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 10px',
                    borderRadius: 999,
                    background: isConnected ? 'rgba(34,197,94,0.85)' : 'rgba(220,38,38,0.85)',
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 600,
                    border: '1px solid rgba(255,255,255,0.08)',
                    backdropFilter: 'blur(6px)',
                  }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />
                    {isConnected ? 'Connected' : 'Reconnecting'}
                  </span>
                </div>
              )}

              {/* Stats overlay (top-left, below badge) */}
              {show?.status === 'LIVE' && (
                <StatsOverlay
                  totalSales={totalSales}
                  orderCount={showSales.length}
                  viewerCount={viewerCount}
                  minimized={statsMinimized}
                  onToggle={() => setStatsMinimized(prev => !prev)}
                />
              )}

              {/* Floating side menu (right side) */}
              {show?.status === 'LIVE' && (
                <FloatingSideMenu
                  onControlsClick={() => setActiveModal('controls')}
                  onShareClick={() => setActiveModal('share')}
                  onNotesClick={() => setActiveModal('notes')}
                  onGiveawayClick={() => setActiveModal('giveaway')}
                  hasActiveGiveaway={!!activeGiveaway}
                />
              )}

              {/* Auction overlay (bottom) — item card + status, plus configure
                  panel for picking params before starting/restarting. The
                  configure panel hides itself while an auction or BIN sale is
                  live. */}
              {show?.status === 'LIVE' && !showEnded && (
                <div style={styles.bottomOverlayStack}>
                  <AuctionOverlay
                    displayItem={displayItem}
                    activeAuction={activeAuction}
                    activeBinSale={activeBinSale}
                    auctionEnded={auctionEnded}
                    recentBids={recentBids}
                    startingPrice={overlayStartingPrice}
                    highBidderAvatarUrl={highBidderAvatarUrl}
                    winnerAvatarUrl={winnerAvatarUrl}
                    onEndBinSale={handleEndBinSale}
                    onOpenInventory={() => {
                      setSelectedItem(null);
                      setSelectedListing(null);
                      setLeftPanelTab('inventory');
                    }}
                    endBinLoading={endBinLoading}
                  />
                  <AuctionControls
                    showId={show?.id || null}
                    selectedItem={selectedItem}
                    selectedListing={selectedListing}
                    onDeckItem={onDeckItem}
                    activeAuction={activeAuction}
                    activeBinSale={activeBinSale}
                    auctionEnded={auctionEnded}
                    onRunAnotherAuction={handleRunAnotherAuction}
                  />
                </div>
              )}

              {/* Show ended overlay */}
              {(show?.status === 'ENDED' || showEnded) && (
                <ShowEndedOverlay
                  totalSales={totalSales}
                  orderCount={showSales.length}
                  peakViewerCount={peakViewerCount.current}
                  onGoToDashboard={() => navigate('/sell/live')}
                />
              )}

              {/* Go Live button for SCHEDULED shows */}
              {show?.status === 'SCHEDULED' && (
                <div style={styles.goLiveOverlay}>
                  <button
                    onClick={handleStartShow}
                    disabled={actionLoading}
                    style={styles.goLiveButton}
                  >
                    {actionLoading ? 'Starting...' : 'Go Live'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Chat / Viewers */}
          <div style={styles.rightColumn}>
            {show?.status === 'ENDED' ? (
              <div style={styles.endedCard}>
                <h3 style={styles.endedCardTitle}>All wrapped up</h3>
                {showEnded && showEnded.reason !== 'host_ended' && (
                  <div style={styles.showEndedBanner}>
                    <AlertTriangle size={20} color="#DC2626" />
                    <div>
                      <p style={styles.showEndedTitle}>Show ended unexpectedly</p>
                      <p style={styles.showEndedText}>
                        {showEnded.reason === 'seller_timeout'
                          ? 'Your show was ended because the server lost your connection. This can happen during deployments or network issues.'
                          : 'Your show was ended by the system. Any completed sales have been saved.'}
                      </p>
                    </div>
                  </div>
                )}
                <button
                  onClick={() => navigate('/sell/live')}
                  style={styles.newShowButton}
                >
                  Create New Show
                </button>
              </div>
            ) : (
              <>
                {/* Tab Switcher */}
                {show?.status === 'LIVE' && (
                  <div style={styles.tabSwitcher}>
                    <button
                      style={{
                        ...styles.tabButton,
                        ...(rightPanelTab === 'chat' ? styles.tabButtonActive : {}),
                      }}
                      onClick={() => setRightPanelTab('chat')}
                    >
                      Chat
                    </button>
                    <button
                      style={{
                        ...styles.tabButton,
                        ...(rightPanelTab === 'viewers' ? styles.tabButtonActive : {}),
                      }}
                      onClick={() => setRightPanelTab('viewers')}
                    >
                      Viewers ({viewerCount})
                    </button>
                  </div>
                )}

                <div style={styles.tabContent}>
                  {show?.status === 'LIVE' ? (
                    rightPanelTab === 'chat' ? (
                      <Chat showId={show?.id} channelId={show?.chatChannelId ?? null} sellerId={show?.sellerId} maxHeight={undefined} />
                    ) : (
                      <ViewerList
                        viewers={viewers}
                        showId={show?.id}
                        isSeller={!!(user && show?.sellerId && user.id === show.sellerId)}
                        currentUserId={user?.id}
                      />
                    )
                  ) : (
                    <p style={styles.storeHint}>Chat will appear when the show is live</p>
                  )}
                </div>
              </>
            )}
          </div>
        </main>
      </div>

      {/* Modals */}
      {activeModal === 'controls' && (
        <ControlsModal
          onClose={() => setActiveModal(null)}
          activeAuction={activeAuction}
          onEndShow={handleEndShow}
          isBroadcasting={isBroadcasting}
          onClaimBroadcast={claimBroadcast}
        />
      )}
      <ShareModal
        isOpen={activeModal === 'share'}
        onClose={() => setActiveModal(null)}
        url={`${window.location.origin}/watch/${show?.id || ''}`}
        title={show?.name || 'Live Show'}
      />
      {activeModal === 'notes' && (
        <SellerNotesModal
          onClose={() => setActiveModal(null)}
          initialNotes={show?.sellerNotes || ''}
          onSave={async (notes) => {
            if (!show) return;
            try {
              await updateSellerNotes(show.id, notes);
              setShow({ ...show, sellerNotes: notes });
            } catch (e) {
              console.error('Failed to save notes:', e);
            }
          }}
        />
      )}
      {activeModal === 'giveaway' && (
        <GiveawayModal
          onClose={() => setActiveModal(null)}
          showId={show?.id || ''}
          listings={listings}
          activeGiveaway={activeGiveaway}
          giveawayEnded={giveawayEnded}
          onGiveawayEnded={clearGiveawayEnded}
        />
      )}
    </HMSRoomProvider>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    backgroundColor: colors.gray[100],
    color: colors.ink.black,
  },
  loadingState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    color: '#666',
  },
  errorState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    gap: '16px',
  },
  errorText: {
    fontSize: '18px',
    color: '#666',
  },
  main: {
    display: 'grid',
    gap: '16px',
    padding: '16px',
    height: 'calc(100vh - 64px)',
    overflow: 'hidden',
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: colors.white,
    borderRadius: 16,
    border: `1px solid ${colors.gray[300]}`,
    padding: '12px',
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    padding: '4px 0',
  },
  bottomOverlayStack: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    padding: '10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    pointerEvents: 'auto',
  },
  centerColumn: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRadius: 16,
  },
  videoSection: {
    flex: 1,
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: '#111',
    borderRadius: 16,
  },
  topGradient: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 80,
    background: 'linear-gradient(to bottom, rgba(0,0,0,0.5), transparent)',
    zIndex: 5,
    pointerEvents: 'none',
  },
  bottomGradient: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 200,
    background: 'linear-gradient(to top, rgba(0,0,0,0.5), transparent)',
    zIndex: 5,
    pointerEvents: 'none',
  },
  liveBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 10px',
    background: '#FF2D55',
    color: '#fff',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.06em',
    borderRadius: 999,
    textTransform: 'uppercase' as const,
  },
  liveBadgeDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: '#fff',
    animation: 'livePulse 1.4s ease-in-out infinite',
  },
  goLiveOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 15,
  },
  goLiveButton: {
    padding: '16px 48px',
    backgroundColor: colors.accent.pink,
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    fontSize: 18,
    fontWeight: 700,
    cursor: 'pointer',
    letterSpacing: '0.02em',
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: colors.white,
    borderRadius: 16,
    border: `1px solid ${colors.gray[300]}`,
    padding: '12px',
  },
  endedCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: '8px 0',
  },
  endedCardTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 600,
  },
  tabSwitcher: {
    display: 'flex',
    backgroundColor: colors.gray[100],
    borderRadius: 10,
    padding: 4,
    marginBottom: 12,
    flexShrink: 0,
  },
  tabButton: {
    flex: 1,
    padding: '8px 12px',
    backgroundColor: 'transparent',
    color: '#666',
    border: 'none',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  tabButtonActive: {
    backgroundColor: colors.gray[900],
    color: '#fff',
  },
  tabContent: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  leftTabSwitcher: {
    display: 'flex',
    backgroundColor: colors.gray[100],
    borderRadius: 10,
    padding: 4,
    marginBottom: 12,
    flexShrink: 0,
  },
  leftTabButton: {
    flex: 1,
    padding: '8px 12px',
    backgroundColor: 'transparent',
    color: '#666',
    border: 'none',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  leftTabButtonActive: {
    backgroundColor: colors.gray[900],
    color: '#fff',
  },
  storeHint: {
    margin: 0,
    fontSize: 13,
    color: '#888',
    textAlign: 'center',
    padding: '24px 0',
  },
  itemsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  storeItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 8,
    borderRadius: 8,
    cursor: 'pointer',
    // Longhand instead of `border` shorthand: when storeItemSelected sets and
    // later un-sets borderColor, React clears the longhand. With shorthand,
    // the original transparent value gets stripped and the border falls back
    // to currentColor (dark text) — leaving a sticky "visited" black border.
    borderWidth: 2,
    borderStyle: 'solid',
    borderColor: 'transparent',
    transition: 'all 0.15s',
  },
  storeItemSelected: {
    backgroundColor: '#EFF6FF',
    borderColor: '#3B82F6',
  },
  storeItemDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  storeItemImage: {
    width: 48,
    height: 48,
    borderRadius: 6,
    objectFit: 'cover',
  },
  storeItemInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  storeItemName: {
    fontSize: 13,
    fontWeight: 500,
  },
  storeItemPrice: {
    fontSize: 12,
    color: '#22C55E',
    fontWeight: 600,
  },
  storeItemStock: {
    fontSize: 11,
    color: '#666',
  },
  storeItemImagePlaceholder: {
    width: 48,
    height: 48,
    borderRadius: 6,
    backgroundColor: '#E5E7EB',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  orderItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 10,
    borderRadius: 8,
    backgroundColor: '#F9FAFB',
    border: '1px solid #E5E7EB',
  },
  orderItemInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  orderItemBuyer: {
    fontSize: 11,
    color: colors.gray[600],
    marginTop: 2,
  },
  orderItemRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 2,
  },
  orderItemPrice: {
    fontSize: 14,
    fontWeight: 600,
    color: '#22C55E',
  },
  orderItemStatus: {
    fontSize: 10,
    fontWeight: 600,
    color: colors.gray[600],
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  failedOrdersSection: {
    backgroundColor: '#FEF2F2',
    borderRadius: 8,
    padding: 8,
    marginBottom: 8,
    border: '1px solid #FECACA',
  },
  failedOrdersHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 8,
    paddingBottom: 6,
    borderBottom: '1px solid #FECACA',
  },
  failedOrdersTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: '#DC2626',
  },
  orderItemFailed: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 8,
    borderRadius: 6,
    backgroundColor: '#FEE2E2',
    marginBottom: 4,
  },
  cancelSaleButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    padding: 0,
    backgroundColor: '#DC2626',
    color: colors.white,
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 11,
    fontWeight: 600,
    marginTop: 4,
  },
  showEndedBanner: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
    padding: '14px 16px',
    backgroundColor: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: 10,
  },
  showEndedTitle: {
    margin: 0,
    fontSize: 14,
    fontWeight: 600,
    color: '#DC2626',
  },
  showEndedText: {
    margin: '4px 0 0',
    fontSize: 13,
    color: colors.gray[700],
    lineHeight: '1.4',
  },
  newShowButton: {
    padding: '12px 24px',
    backgroundColor: colors.gray[300],
    color: colors.ink.black,
    border: 'none',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
  },
  secondaryButton: {
    padding: '12px 24px',
    backgroundColor: colors.gray[300],
    color: colors.ink.black,
    border: 'none',
    borderRadius: 8,
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
  },
  error: {
    color: '#DC2626',
    fontSize: 13,
    margin: '0 0 12px 0',
  },
  onboardingErrorCard: {
    backgroundColor: '#FEF2F2',
    borderRadius: 12,
    padding: 16,
    border: '1px solid #FECACA',
    marginBottom: 16,
  },
  onboardingErrorHeader: {
    display: 'flex',
    gap: 12,
    marginBottom: 16,
  },
  onboardingErrorIcon: {
    width: 48,
    height: 48,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FEE2E2',
    borderRadius: 12,
    flexShrink: 0,
  },
  onboardingErrorTitle: {
    margin: '0 0 4px 0',
    fontSize: 16,
    fontWeight: 600,
    color: '#DC2626',
  },
  onboardingErrorText: {
    margin: 0,
    fontSize: 14,
    color: '#7F1D1D',
    lineHeight: 1.5,
  },
  onboardingErrorButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
    padding: 12,
    fontSize: 15,
    fontWeight: 600,
    color: '#fff',
    backgroundColor: '#DC2626',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    textDecoration: 'none',
  },
};
